import { useEffect, useRef, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "mapbox-gl-compare/dist/mapbox-gl-compare.css";
import useDarkMode from "use-dark-mode";
import { toPng } from "html-to-image";
import { useHistory } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  WizardCard,
  LayerLegend,
  ParcelDetailPanel,
  ExploreToolbar,
  ExploreLayerPanel,
} from "./components";
import {
  WIZARD_STEPS,
  CAMERA_POSITIONS,
  EXPLORE_PAN_BOUNDS,
  FEATURED_PARCEL_ID,
  LAYER_SOURCES,
} from "./config/agricultural-lulc";
import type { CropVariant } from "./types/agricultural-lulc";
import { buildParcelAttribution } from "./utils/parcel-attribution";
import {
  loadGeoJSONIntoSource,
  registerMainMapLayers,
} from "./map/register-layers";
import { fetchSource } from "./utils/geojson-cache";
import { applyLayerState } from "./map/apply-layer-state";
import {
  setLayerVisibility,
  highlightParcelOn,
  clearParcelHighlightOn,
} from "./map/map-helpers";
import {
  getFeatureByPid,
  queryLayerDataForParcel,
} from "./utils/geojson-cache";
import { useComparisonMap } from "./hooks/use-comparison-map";
import type {
  DemoYear,
  DemoLayerStates,
  BasemapStyle,
  ParcelAttribution,
} from "./types/agricultural-lulc";

// ─── Mapbox styles ──────────────────────────────────────────────────
const STYLE_DARK = "mapbox://styles/geoscape-psma/clpt56v2100ey01r84bav7gn1";
const STYLE_LIGHT = "mapbox://styles/geoscape-psma/clpt55jcl00f201px05cd3bf1";
const SATELLITE_STYLE = "mapbox://styles/mapbox/satellite-v9";

// The satellite step always uses the higher-resolution z15 Sentinel tiles
// served from S3.
const SENTINEL_VARIANT = "hires" as const;
// Forest layer always renders the 10 m pixel raster from out/2024/forest.tif —
// the parcel-aggregated GeoJSON variant exists in the type but is no longer
// reachable from the UI.
const FOREST_USE_RASTER = true;

// Year-toggle cycle for the explore toolbar: 2024 → 2025 → change → 2024 …
const NEXT_YEAR: Record<DemoYear, DemoYear> = {
  2024: 2025,
  2025: "change",
  change: 2024,
};

// ─── Default layer state ────────────────────────────────────────────
// Thematic layers default to their pixel-level rasters. The parcel-
// aggregated GeoJSON variants exist on the map but aren't reachable from
// the wizard UI any more — they only get opted into via internal state.
const DEFAULT_LAYERS: DemoLayerStates = {
  lgaBoundary: { visible: true, opacity: 1 },
  parcels: { visible: false, opacity: 0.8 },
  sentinel: { visible: false, opacity: 1, year: 2025 },
  lulc: { visible: false, opacity: 0.6, year: 2025, useRaster: true },
  crops: { visible: false, opacity: 0.7, variant: "crops" },
  forest: { visible: false, opacity: 0.7, useRaster: true },
};

export function AgriculturalLulcPage() {
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const compareContainerRef = useRef<HTMLDivElement>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const beforeMapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const { value: isDark } = useDarkMode(false, { classNameDark: "dark" });
  const history = useHistory();
  const { showNewMessage } = useIntercom();
  const handleSpeakToExpert = () =>
    showNewMessage("I'd like to learn more about LULC");

  // ─── State ──────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardDismissed, setWizardDismissed] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [year, setYear] = useState<DemoYear>(2025);
  const [layers, setLayers] = useState<DemoLayerStates>(DEFAULT_LAYERS);
  const [basemap, setBasemap] = useState<BasemapStyle>("default");
  const [selectedParcel, setSelectedParcel] = useState<ParcelAttribution | null>(null);
  const [detailPanelVisible, setDetailPanelVisible] = useState(true);
  const [layerPanelVisible, setLayerPanelVisible] = useState(true);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [showPatches, setShowPatches] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  // Bumped every time the basemap style finishes (re)loading. Visibility
  // effects depend on this so they re-apply after `registerMainMapLayers` has
  // rebuilt sources/layers from scratch.
  const [styleEpoch, setStyleEpoch] = useState(0);

  // Track which basemap style is currently active
  const currentStyleRef = useRef<string>("");

  // ─── Resolve which style URL to use ─────────────────────────────
  const getThemeStyle = useCallback(() => {
    return isDark ? STYLE_DARK : STYLE_LIGHT;
  }, [isDark]);

  // ─── Comparison-map lifecycle (extracted hook) ──────────────────
  const { beforeMap } = useComparisonMap({
    enabled: comparisonMode,
    mainMap: map,
    beforeMapContainer,
    compareContainer: compareContainerRef,
    layers,
    currentStyleRef,
    fallbackStyle: SATELLITE_STYLE,
    onParcelSelected: setSelectedParcel,
    onParcelHighlight: (pid) => highlightParcelOn([map.current, beforeMap.current], pid),
  });

  // Mirror onto the before map too so the yellow outline shows on whichever
  // side of the swipe the parcel was clicked from.
  const highlightParcel = (pid: string) =>
    highlightParcelOn([map.current, beforeMap.current], pid);
  const clearParcelHighlight = () =>
    clearParcelHighlightOn([map.current, beforeMap.current]);

  // Single source of truth for crop variant — lives on layers.crops.variant.
  // Wrapped so wizard-card and explore-panel can flip it without a separate
  // setCropVariant atom that needs an effect to sync the two.
  const cropVariant: CropVariant = layers.crops.variant ?? "crops";
  const setCropVariant = (v: CropVariant) =>
    setLayers((prev) =>
      prev.crops.variant === v ? prev : { ...prev, crops: { ...prev.crops, variant: v } }
    );

  // ─── Initialize main map ────────────────────────────────────────
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    mapboxgl.accessToken = `${import.meta.env.VITE_MAPBOXGL_TOKEN}`;

    const initialStyle = getThemeStyle();
    currentStyleRef.current = initialStyle;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: initialStyle,
      center: CAMERA_POSITIONS.challenge.center,
      zoom: CAMERA_POSITIONS.challenge.zoom,
      // Hi-res Sentinel tiles top out at z15. Capping the camera at z16
      // limits Mapbox to one zoom of overzoom past native, which is barely
      // perceptible. Going further would make the imagery visibly blocky.
      maxZoom: 16,
      // Required so the explore-toolbar screenshot button can read the
      // WebGL canvas via toDataURL() — otherwise the buffer is cleared
      // after each frame and the export comes out blank.
      preserveDrawingBuffer: true,
    });

    map.current.on("style.load", async () => {
      const m = map.current;
      if (!m) return;
      // Fetch boundary first — tiny (87 KB) and required for the LGA outline
      // visible from step 1. Every other GeoJSON registers with EMPTY_FC
      // and gets streamed in on demand by the per-step effect below.
      await fetchSource(LAYER_SOURCES.lgaBoundary);
      registerMainMapLayers(m);
      setMapReady(true);
      setStyleEpoch((n) => n + 1);
    });

    // Parcel click handler
    map.current.on("click", "parcels-fill", (e) => {
      const feature = e.features?.[0];
      if (!feature || !map.current) return;
      const pid = feature.properties?.cadastre_polygon_pid;
      const areaSqm = feature.properties?.area;
      if (pid) {
        const layerData = queryLayerDataForParcel(pid);
        const attribution = buildParcelAttribution(pid, areaSqm ?? 0, layerData);
        setSelectedParcel(attribution);
        highlightParcel(pid);
      }
    });

    map.current.on("mouseenter", "parcels-fill", () => {
      if (map.current) map.current.getCanvas().style.cursor = "pointer";
    });
    map.current.on("mouseleave", "parcels-fill", () => {
      if (map.current) map.current.getCanvas().style.cursor = "";
    });

    if (mapContainer.current) {
      resizeObserver.current = new ResizeObserver(() => {
        requestAnimationFrame(() => map.current?.resize());
      });
      resizeObserver.current.observe(mapContainer.current);
    }

    return () => {
      resizeObserver.current?.disconnect();
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // Mount-only: the map is constructed once and torn down on unmount. Theme
    // changes are handled by a separate effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Theme changes ──────────────────────────────────────────────
  useEffect(() => {
    if (!map.current) return;
    if (basemap === "satellite") return;

    const styleUrl = getThemeStyle();
    if (styleUrl && styleUrl !== currentStyleRef.current) {
      currentStyleRef.current = styleUrl;
      map.current.setStyle(styleUrl);
    }
  }, [isDark, basemap, getThemeStyle]);

  // ─── Apply layer state whenever it changes ──────────────────────
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    applyLayerState(m, layers);
  }, [layers, mapReady]);

  // ─── Lazy-load thematic GeoJSONs per wizard step ────────────────
  // First-visit total (~9 MB across 7 files) is too big to fetch upfront.
  // Each step only needs at most 2-3 layers, so we stream them in on entry.
  // The cache + in-flight dedup in geojson-cache means re-running this on
  // styleEpoch (after a basemap swap rebuilt the layer stack) is free —
  // it just calls `setData` with already-resolved FCs to repopulate the
  // newly-registered sources.
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    const stepId = WIZARD_STEPS[currentStep]?.id;
    const want = new Set<string>();

    // Always have parcels available once we're past the intro — both for
    // step 6 (featured parcel) and so click-to-inspect works in steps where
    // the line layer is visible. Pre-fetch from step 3 onwards so it lands
    // in cache well before the user clicks Next on step 5.
    const stepIndex = WIZARD_STEPS.findIndex((s) => s.id === stepId);
    if (wizardDismissed || stepIndex >= 2) want.add(LAYER_SOURCES.parcels);

    if (stepId === "lulc" || stepId === "parcel" || wizardDismissed) {
      want.add(LAYER_SOURCES.lulc2024);
      want.add(LAYER_SOURCES.lulc2025);
    }
    if (stepId === "crops" || wizardDismissed) {
      want.add(LAYER_SOURCES.crops2025);
    }
    if (stepId === "forest" || wizardDismissed) {
      want.add(LAYER_SOURCES.forest2025);
    }
    if (stepId === "comparison" || wizardDismissed) {
      // Comparison swipe pins LULC 2024 ↔ 2025 plus the change overlay.
      want.add(LAYER_SOURCES.lulc2024);
      want.add(LAYER_SOURCES.lulc2025);
      want.add(LAYER_SOURCES.lulcChange);
      want.add(LAYER_SOURCES.lulcChangePatches);
    }

    for (const url of want) {
      void loadGeoJSONIntoSource(m, url);
    }
  }, [currentStep, wizardDismissed, mapReady, styleEpoch]);

  // ─── Sentinel raster: visible on the "Satellite Imagery" wizard step ──
  // and in explore mode when the user toggles it on. During the wizard the
  // active year follows the wizard year toggle; in explore mode it follows
  // `layers.sentinel.year`. Sentinel is registered *below* the LULC raster
  // pyramid, so LULC paints on top during comparison without further gating.
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    const stepId = WIZARD_STEPS[currentStep]?.id;
    const inWizardSatellite = !wizardDismissed && stepId === "satellite";
    const inExplore = wizardDismissed && layers.sentinel.visible;
    const sentinelVisible = inWizardSatellite || inExplore;
    const activeYear: "2024" | "2025" = inExplore
      ? (String(layers.sentinel.year) as "2024" | "2025")
      : year === 2024 ? "2024" : "2025";
    const opacity = inExplore ? layers.sentinel.opacity : 1;
    for (const variant of ["default", "hires"] as const) {
      for (const y of ["2024", "2025"] as const) {
        const layerId = `sentinel-${variant}-${y}-raster`;
        if (!m.getLayer(layerId)) continue;
        const showThisOne =
          sentinelVisible && variant === SENTINEL_VARIANT && y === activeYear;
        setLayerVisibility(m, layerId, showThisOne);
        m.setPaintProperty(layerId, "raster-opacity", showThisOne ? opacity : 0);
      }
    }
  }, [
    currentStep,
    wizardDismissed,
    year,
    mapReady,
    styleEpoch,
    layers.sentinel.visible,
    layers.sentinel.year,
    layers.sentinel.opacity,
  ]);

  // ─── Patches overlay: visible only when change mode + toggle on ──
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    // Patches are an overlay for the parcel-aggregated GeoJSON change layer.
    // The raster change layer already shows real per-pixel change, so suppress
    // patches whenever raster mode is active.
    const showIt =
      showPatches &&
      layers.lulc.visible &&
      layers.lulc.year === "change" &&
      !layers.lulc.useRaster;
    if (m.getLayer("lulc-change-patches-fill")) {
      setLayerVisibility(m, "lulc-change-patches-fill", showIt);
      m.setPaintProperty("lulc-change-patches-fill", "fill-opacity", showIt ? 0.7 : 0);
    }
  }, [
    showPatches,
    layers.lulc.visible,
    layers.lulc.year,
    layers.lulc.useRaster,
    mapReady,
  ]);

  // ─── Step transition: compute target layers + camera per step ───
  const applyStep = useCallback(
    (stepIndex: number) => {
      const step = WIZARD_STEPS[stepIndex];
      const m = map.current;

      // Camera fly + per-step zoom envelope
      const cam = CAMERA_POSITIONS[step.id];
      if (m && cam) {
        // Widen the envelope before flying — otherwise setMinZoom/MaxZoom
        // can clamp the active zoom and yank the viewport mid-fly. The
        // tight per-step limits get re-applied once the fly settles.
        m.setMinZoom(0);
        m.setMaxZoom(22);
        m.flyTo({
          center: cam.center,
          zoom: cam.zoom,
          duration: 1500,
          essential: true,
        });
        m.once("moveend", () => {
          m.setMinZoom(cam.minZoom);
          m.setMaxZoom(cam.maxZoom);
        });
      }

      // The wizard always uses the default themed basemap. The Sentinel
      // raster (step 2) and the LULC/Crops/Forest rasters render on top of
      // it — no auto-switch to mapbox satellite anywhere in the wizard.
      if (basemap === "satellite") {
        switchBasemap("default");
      }

      // Determine target layer state per step
      const target: DemoLayerStates = {
        lgaBoundary: { visible: true, opacity: 1 },
        parcels: { visible: false, opacity: 0.8 },
        sentinel: { visible: false, opacity: 1, year: year === 2024 ? 2024 : 2025 },
        lulc: { visible: false, opacity: 0.6, year, useRaster: false },
        crops: { visible: false, opacity: 0.7, variant: cropVariant },
        forest: { visible: false, opacity: 0.7, useRaster: FOREST_USE_RASTER },
      };

      switch (step.id) {
        case "challenge":
          break;
        case "satellite":
          // Opens with 2024 by design — force it on entry so the step is
          // reproducible regardless of where the user last left `year`.
          if (year !== 2024) setYear(2024);
          break;
        case "lulc":
          if (year !== 2024) setYear(2024);
          target.lulc.year = 2024;
          target.sentinel.year = 2024;
          target.lulc.visible = true;
          target.lulc.opacity = 0.7;
          target.lulc.useRaster = true;
          break;
        case "crops":
          target.crops.visible = true;
          target.crops.opacity = 0.7;
          break;
        case "forest":
          target.forest.visible = true;
          target.forest.opacity = 0.7;
          break;
        case "parcel": {
          // Property-level step always shows 2025 LULC under the highlight —
          // "change" or 2024 leaking from earlier steps would otherwise paint
          // only the changed parcels (≈184) and look broken at z14.
          if (year !== 2025) setYear(2025);
          target.lulc.year = 2025;
          target.lulc.useRaster = true;
          target.parcels.visible = true;
          target.lulc.visible = true;
          target.lulc.opacity = 0.4;
          const layerData = queryLayerDataForParcel(FEATURED_PARCEL_ID);
          const featuredFeature = getFeatureByPid(LAYER_SOURCES.parcels, FEATURED_PARCEL_ID);
          const featuredArea = (featuredFeature?.properties?.area as number | undefined) ?? 0;
          setSelectedParcel(buildParcelAttribution(FEATURED_PARCEL_ID, featuredArea, layerData));
          // Wait for the camera fly to settle before painting the highlight,
          // otherwise the outline can flash mid-fly while parcels-fill is
          // still being added to the new viewport.
          if (m) m.once("moveend", () => highlightParcel(FEATURED_PARCEL_ID));
          break;
        }
        case "comparison":
          // Both sides of the swipe show the 10 m LULC raster (2024 ↔ 2025).
          // The main map carries 2025 via applyLayerState; the before map is
          // configured by useComparisonMap. Parcel outlines are intentionally
          // off — the raster is the focal point. Pin year to 2025 so the
          // right-hand side doesn't inherit a stale selection.
          if (year !== 2025) setYear(2025);
          target.lulc.year = 2025;
          target.lulc.visible = true;
          target.lulc.opacity = 0.7;
          target.lulc.useRaster = true;
          enableComparisonMode();
          break;
        case "explore":
          target.parcels.visible = true;
          target.lulc.visible = true;
          target.lulc.opacity = 0.5;
          break;
      }

      setLayers(target);

      // Clean up parcel selection when leaving parcel step (but keep it
      // around when the user arrives on the comparison step from parcel).
      if (step.id !== "parcel" && step.id !== "comparison") {
        setSelectedParcel(null);
        clearParcelHighlight();
      }

      // Turn off comparison when not on that step
      if (step.id !== "comparison") {
        setComparisonMode(false);
      }
    },
    // applyStep also reads `cropVariant` and calls `setCropVariant` indirectly
    // via setLayers updates, but rebinding it on every variant flip would
    // re-trigger the per-step camera fly. Cropping it to the deps that should
    // really replay the step is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [year, basemap, highlightParcel, clearParcelHighlight]
  );

  // ─── Apply step when it changes ─────────────────────────────────
  useEffect(() => {
    if (mapReady && !wizardDismissed) {
      applyStep(currentStep);
    }
    // Step replay should fire on currentStep / mapReady only — applyStep's
    // identity flips on year/basemap changes that have their own effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, mapReady]);


  // ─── Navigation lock during the wizard ──────────────────────────
  // Each wizard step camera is hand-picked. The default rule: pan is
  // disabled, zoom stays on so users can inspect detail.
  //  * Step 1 (challenge): everything off — pure static intro view.
  //  * Step 7 (comparison): everything off — the user only drives the
  //    swipe handle.
  //  * Explore mode: everything on.
  useEffect(() => {
    const m = map.current;
    if (!m || !mapReady) return;
    const stepId = WIZARD_STEPS[currentStep]?.id;

    const panEnabled = wizardDismissed;
    const zoomEnabled =
      wizardDismissed || (stepId !== "challenge" && stepId !== "comparison");

    if (panEnabled) m.dragPan.enable();
    else m.dragPan.disable();

    if (zoomEnabled) {
      m.scrollZoom.enable();
      m.doubleClickZoom.enable();
      m.keyboard.enable();
      m.touchZoomRotate.enable();
    } else {
      m.scrollZoom.disable();
      m.doubleClickZoom.disable();
      m.keyboard.disable();
      m.touchZoomRotate.disable();
    }
  }, [wizardDismissed, mapReady, currentStep]);

  // ─── Basemap switching ──────────────────────────────────────────
  const switchBasemap = useCallback(
    (style: BasemapStyle) => {
      const m = map.current;
      if (!m) return;

      const newStyle = style === "satellite" ? SATELLITE_STYLE : getThemeStyle();

      if (newStyle !== currentStyleRef.current) {
        currentStyleRef.current = newStyle;
        m.setStyle(newStyle);
      }
      setBasemap(style);
    },
    [getThemeStyle]
  );

  // ─── Year change ────────────────────────────────────────────────
  const handleYearChange = useCallback((newYear: DemoYear) => {
    setYear(newYear);
    setLayers((prev) => ({
      ...prev,
      lulc: { ...prev.lulc, year: newYear },
    }));
  }, []);

  // Comparison is pinned to LULC 2024 (left) ↔ 2025 (right). Force the LULC
  // layer on and snap year off "change" (which has no temporal counterpart).
  // The actual before-map/Compare-control lifecycle lives in useComparisonMap.
  const enableComparisonMode = () => {
    if (year === "change") setYear(2025);
    setLayers((prev) => ({
      ...prev,
      lulc: {
        ...prev.lulc,
        visible: true,
        year: prev.lulc.year === "change" ? 2025 : prev.lulc.year,
        useRaster: true,
      },
    }));
    setComparisonMode(true);
  };

  // ─── Wizard navigation ──────────────────────────────────────────
  const handleNext = () => {
    const isLastStep = currentStep === WIZARD_STEPS.length - 1;
    if (isLastStep) {
      enterExploreMode();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const enterExploreMode = () => {
    setWizardDismissed(true);
    setComparisonMode(false);
    setSelectedParcel(null);
    clearParcelHighlight();

    // Forest is the entry layer — explore opens with the forest raster
    // visible and the other thematic layers off. Crops keeps whatever raster
    // variant the user last picked.
    const exploreCropVariant: CropVariant =
      cropVariant === "parcel" ? "crops" : cropVariant;
    setYear(2025);
    setLayers({
      lgaBoundary: { visible: true, opacity: 1 },
      parcels: { visible: true, opacity: 0.8 },
      sentinel: { visible: true, opacity: 1, year: 2025 },
      lulc: { visible: true, opacity: 0.5, year: 2025, useRaster: true },
      crops: { visible: false, opacity: 0.7, variant: exploreCropVariant },
      forest: { visible: false, opacity: 0.7, useRaster: true },
    });
    setLayerPanelVisible(true);

    // Restore the global zoom envelope — the wizard had it clamped to a
    // narrow band per step. minZoom matches the LULC raster pyramid's lowest
    // tile level so thematic layers always have something to draw. Pan
    // bounds keep the LGA in frame.
    map.current?.setMinZoom(CAMERA_POSITIONS.explore.minZoom);
    map.current?.setMaxZoom(CAMERA_POSITIONS.explore.maxZoom);
    map.current?.setMaxBounds(EXPLORE_PAN_BOUNDS);
    map.current?.flyTo({
      center: CAMERA_POSITIONS.explore.center,
      zoom: CAMERA_POSITIONS.explore.zoom,
      duration: 1500,
      essential: true,
    });
  };

  // ─── Close dialog handlers ──────────────────────────────────────
  const handleCloseClick = () => setCloseDialogOpen(true);
  const handleExploreOnly = () => {
    setCloseDialogOpen(false);
    enterExploreMode();
  };
  const handleLeaveDemo = () => {
    setCloseDialogOpen(false);
    history.push("/demos");
  };
  const handleContinueWizard = () => setCloseDialogOpen(false);

  // ─── Explore mode layer controls ────────────────────────────────
  const toggleLayer = (key: keyof DemoLayerStates) => {
    setLayers((prev) => {
      const next = { ...prev, [key]: { ...prev[key], visible: !prev[key].visible } };
      // Turning parcels off implies the user no longer wants the per-parcel
      // selection either — clear the highlight and detail panel together so
      // they don't linger over a layer that's no longer drawn.
      if (key === "parcels" && prev.parcels.visible) {
        setSelectedParcel(null);
        clearParcelHighlight();
      }
      return next;
    });
  };

  const setLayerOpacity = (key: keyof DemoLayerStates, opacity: number) => {
    setLayers((prev) => ({
      ...prev,
      [key]: { ...prev[key], opacity },
    }));
  };

  const setSentinelYear = (sentinelYear: 2024 | 2025) => {
    setLayers((prev) => ({
      ...prev,
      sentinel: { ...prev.sentinel, year: sentinelYear },
    }));
  };

  // ─── Derived state ──────────────────────────────────────────────
  const step = WIZARD_STEPS[currentStep];
  const stepId = step?.id;
  const showWizardLegend = !wizardDismissed && Boolean(step?.showLegend);
  const showParcelPanel =
    selectedParcel && (wizardDismissed ? detailPanelVisible : stepId === "parcel");

  return (
    <div ref={fullscreenContainerRef} className="absolute inset-0 w-full h-full bg-background">
      {/* Map wrapper — mapbox-gl-compare needs both maps inside a shared container.
           The before map (A) must be AFTER the main map (B) in the DOM so it stacks
           on top — the plugin clips both maps, and A (left) needs to be visually above B. */}
      <div ref={compareContainerRef} data-compare-wrapper className="absolute inset-0 w-full h-full">
        {/* Main / after map (2025) — rendered first (behind).
             Inline position:absolute overrides mapbox-gl.css's .mapboxgl-map { position: relative }
             which is critical because the compare plugin uses CSS clip (requires position: absolute). */}
        <div ref={mapContainer} className="absolute inset-0 w-full h-full" style={{ position: "absolute" }} />
        {/* Before map (2024) — rendered second (on top), hidden until comparison mode.
             Uses visibility:hidden (not display:none) so the container always has
             real dimensions for Mapbox GL to render into. */}
        <div
          ref={beforeMapContainer}
          className="absolute inset-0 w-full h-full"
          style={{ position: "absolute", visibility: "hidden", pointerEvents: "none" }}
        />
      </div>

      {/* Year labels shown during comparison mode */}
      {comparisonMode && (
        <>
          <div className="absolute top-16 left-1/2 -translate-x-[calc(50%+6rem)] z-30 pointer-events-none">
            <span className="bg-card/90 backdrop-blur text-xs font-semibold text-foreground px-3 py-1.5 rounded-full border border-border shadow-sm">
              LULC 2024
            </span>
          </div>
          <div className="absolute top-16 left-1/2 translate-x-[calc(50%-3rem+6rem)] z-30 pointer-events-none">
            <span className="bg-card/90 backdrop-blur text-xs font-semibold text-foreground px-3 py-1.5 rounded-full border border-border shadow-sm">
              LULC 2025
            </span>
          </div>
        </>
      )}

      {/* ─── Wizard Card ──────────────────────────────────────────── */}
      {!wizardDismissed && (
        <div className="absolute bottom-6 left-4 z-20">
          <WizardCard
            currentStep={currentStep}
            year={year}
            showPatches={showPatches}
            cropVariant={cropVariant}
            onYearChange={handleYearChange}
            onTogglePatches={() => setShowPatches((v) => !v)}
            onCropVariantChange={setCropVariant}
            onNext={handleNext}
            onBack={handleBack}
            onClose={handleCloseClick}
            onSpeakToExpert={handleSpeakToExpert}
          />
        </div>
      )}

      {/* ─── Legend (wizard mode) — positioned to the right of the wizard card */}
      {showWizardLegend && (
        <div className="absolute bottom-6 left-[28.5rem] z-20">
          <LayerLegend layers={layers} />
        </div>
      )}

      {/* ─── Parcel Detail Panel ──────────────────────────────────── */}
      {showParcelPanel && (
        <div className="absolute top-[5rem] group-has-data-[collapsible=icon]/sidebar-wrapper:top-[4rem] right-4 z-20">
          <ParcelDetailPanel
            parcel={selectedParcel}
            onClose={() => {
              setSelectedParcel(null);
              clearParcelHighlight();
            }}
          />
        </div>
      )}

      {/* ─── Explore Mode UI ──────────────────────────────────────── */}
      {wizardDismissed && (
        <>
          {layerPanelVisible && (
            <div className="absolute top-[5rem] group-has-data-[collapsible=icon]/sidebar-wrapper:top-[4rem] left-4 z-20">
              <ExploreLayerPanel
                layers={layers}
                cropVariant={cropVariant}
                comparisonMode={comparisonMode}
                onToggleLayer={toggleLayer}
                onOpacityChange={setLayerOpacity}
                onCropVariantChange={setCropVariant}
                onSentinelYearChange={setSentinelYear}
                onSpeakToExpert={handleSpeakToExpert}
              />
            </div>
          )}

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
            <ExploreToolbar
              layerPanelVisible={layerPanelVisible}
              detailPanelVisible={detailPanelVisible}
              comparisonMode={comparisonMode}
              lulcVisible={layers.lulc.visible}
              year={year}
              onToggleLayerPanel={() => setLayerPanelVisible((v) => !v)}
              onToggleDetailPanel={() => setDetailPanelVisible((v) => !v)}
              onToggleComparison={() => {
                if (comparisonMode) setComparisonMode(false);
                else enableComparisonMode();
              }}
              onToggleYear={() => handleYearChange(NEXT_YEAR[year])}
              onZoomIn={() => map.current?.zoomIn({ duration: 300 })}
              onZoomOut={() => map.current?.zoomOut({ duration: 300 })}
              onRecenter={() => {
                map.current?.flyTo({
                  center: CAMERA_POSITIONS.explore.center,
                  zoom: CAMERA_POSITIONS.explore.zoom,
                  duration: 1500,
                });
              }}
              onScreenshot={async () => {
                const node = fullscreenContainerRef.current;
                if (!node) return;
                // Force Mapbox to render once so the WebGL buffer matches the
                // current frame before html-to-image samples the canvas.
                map.current?.triggerRepaint();
                await new Promise((r) => requestAnimationFrame(r));
                const dataUrl = await toPng(node, {
                  cacheBust: true,
                  pixelRatio: window.devicePixelRatio,
                });
                const link = document.createElement("a");
                link.download = "lulc-screenshot.png";
                link.href = dataUrl;
                link.click();
              }}
              onFullscreen={() => {
                if (!document.fullscreenElement) {
                  fullscreenContainerRef.current?.requestFullscreen();
                } else {
                  document.exitFullscreen();
                }
              }}
            />
          </div>
        </>
      )}

      {/* ─── Close Confirmation Dialog ────────────────────────────── */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close the wizard?</DialogTitle>
            <DialogDescription>
              Choose what you&apos;d like to do next.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button variant="default" onClick={handleExploreOnly}>
              Close the wizard and just explore the data
            </Button>
            <Button variant="outline" onClick={handleLeaveDemo}>
              Leave the demo and go back to the listing page
            </Button>
            <Button variant="ghost" onClick={handleContinueWizard}>
              Continue with the wizard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AgriculturalLulcPage;
